/**
 * /charte-editoriale — Charte éditoriale de CODE 2 ROUES.
 *
 * Positionnement clair : magazine pédagogique moto indépendant, à but non
 * lucratif, PAS un préparateur ETM. Cite explicitement les sources officielles
 * utilisées (ONISR, REMC, Code de la route, Sécurité Routière) et affirme le
 * respect des marques concurrentes (Rousseau, ENPC, Ediser, Prevent).
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  ShieldCheck,
  BookOpen,
  Newspaper,
  Landmark,
  Users,
  Lock,
  Sparkles,
  ExternalLink,
  Scale,
  Award,
  Heart,
  AlertTriangle,
  FileText,
} from 'lucide-react';
import useSeo from '../hooks/useSeo';
import { buildBreadcrumb, combineJsonLd } from '../seo/breadcrumbs';

const SOURCES_OFFICIELLES = [
  {
    label: 'ONISR — Observatoire National Interministériel de la Sécurité Routière',
    role: "Statistiques d'accidentalité, baromètres mensuels, bilans annuels — publiées sous licence ouverte.",
    url: 'https://www.onisr.securite-routiere.gouv.fr/',
    icon: Landmark,
  },
  {
    label: "REMC — Référentiel de l'Éducation à la Mobilité Citoyenne",
    role: "Arrêté du 23 avril 2012 modifié. Base pédagogique officielle pour tous les enseignants ECSR.",
    url: 'https://www.legifrance.gouv.fr/',
    icon: FileText,
  },
  {
    label: 'Sécurité routière — Ministère de l\'Intérieur',
    role: "Campagnes officielles, vidéos, communiqués. Sources publiques citées et créditées à chaque usage.",
    url: 'https://www.securite-routiere.gouv.fr/',
    icon: ShieldCheck,
  },
  {
    label: 'Code de la route — Légifrance',
    role: "Texte officiel de la loi française. Article R412-1 et suivants. Citations littérales possibles.",
    url: 'https://www.legifrance.gouv.fr/codes/texte_lc/LEGITEXT000006074228/',
    icon: Scale,
  },
  {
    label: 'data.gouv.fr — Portail des données publiques',
    role: "Base BAAC (Bulletin Analyse Accidents Corporels), open data officielle du ministère.",
    url: 'https://www.data.gouv.fr/',
    icon: Landmark,
  },
];

const ENGAGEMENTS = [
  {
    icon: BookOpen,
    title: 'Contenu original',
    body: "Chaque dossier, enquête et livret est rédigé de manière originale par Bruno LLORENS, ECSR diplômé d'État. Aucune reproduction non autorisée de contenus tiers.",
  },
  {
    icon: Landmark,
    title: 'Sources officielles uniquement',
    body: "Toutes nos données statistiques, réglementaires et pédagogiques proviennent exclusivement de sources publiques officielles (ONISR, REMC, Sécurité Routière, Code de la route).",
  },
  {
    icon: Scale,
    title: 'Neutralité commerciale',
    body: "Aucune publicité, aucun tracking commercial, aucun partenariat rémunéré avec les éditeurs de préparation ETM. Le magazine est financé par notre activité ECSR de proximité.",
  },
  {
    icon: Users,
    title: 'Accessibilité pour tous',
    body: "Contenus gratuits, sans inscription obligatoire, sans paywall. Adaptés aux collégiens (ASSR), aux futurs motards (AM, A1, A2), aux enseignants ECSR et aux visiteurs curieux.",
  },
  {
    icon: Sparkles,
    title: 'Ton pédagogique et bienveillant',
    body: "Nous privilégions la compréhension à la mémorisation, le récit magazine à la banque de QCM. Le but : donner du sens à la sécurité routière moto.",
  },
  {
    icon: Award,
    title: 'Transparence des mises à jour',
    body: "Chaque article indique sa date de publication et ses sources datées. Les chiffres ONISR sont rafraîchis à chaque nouveau baromètre officiel.",
  },
];

const CE_QUE_NOUS_SOMMES = [
  { ok: true, text: 'Un magazine pédagogique moto indépendant' },
  { ok: true, text: 'Des dossiers, enquêtes, livrets plateau & circulation, tutos vidéo' },
  { ok: true, text: 'Un site 100 % gratuit, sans pub, sans tracking commercial' },
  { ok: true, text: "Un outil d'accompagnement pour compléter la formation en auto-école" },
  { ok: true, text: "Un projet à but non lucratif porté par un ECSR diplômé d'État" },
];

const CE_QUE_NOUS_NE_SOMMES_PAS = [
  { ok: false, text: 'Un préparateur officiel à l\'ETM (Épreuve Théorique Moto)' },
  { ok: false, text: 'Une banque de questions QCM à visée d\'entraînement examen' },
  { ok: false, text: 'Un concurrent commercial de Codes Rousseau®, ENPC®, Ediser® ou Prevent®' },
  { ok: false, text: 'Un centre agréé pour faire passer l\'examen ETM (rôle des opérateurs d\'État)' },
  { ok: false, text: 'Une auto-école (nos livrets sont un complément, pas un substitut)' },
];

export default function CharteEditoriale() {
  useEffect(() => {
    document.title = 'Notre charte éditoriale · CODE 2 ROUES';
  }, []);

  useSeo({
    title: 'Notre charte éditoriale — CODE 2 ROUES',
    description:
      "La charte éditoriale de CODE 2 ROUES : magazine pédagogique moto indépendant, sources officielles ONISR/REMC/Sécurité Routière, neutralité commerciale, contenus originaux ECSR.",
    keywords:
      'charte éditoriale, magazine moto, sources officielles ONISR, REMC, ECSR indépendant, code 2 roues transparence',
    path: '/charte-editoriale',
    jsonLd: combineJsonLd(
      buildBreadcrumb([['Charte éditoriale', '/charte-editoriale']]),
    ),
  });

  return (
    <main
      className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-10"
      data-testid="page-charte-editoriale"
    >
      {/* Retour + kicker */}
      <div>
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors mb-4"
          data-testid="charte-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour à l'accueil
        </Link>

        <div className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary-soft))] px-3 py-1 text-[10px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary-dark))] mb-3">
          <Newspaper className="w-3.5 h-3.5" />
          Notre engagement éditorial
        </div>
        <h1
          className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.05] mb-4"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          Notre charte éditoriale
        </h1>
        <p className="text-base sm:text-lg text-foreground/75 leading-relaxed max-w-2xl">
          CODE 2 ROUES est un <strong>magazine pédagogique moto indépendant</strong>, à but non lucratif,
          édité par un moniteur ECSR diplômé d'État. Cette charte définit clairement notre positionnement,
          nos sources, nos engagements — et ce que nous <strong>ne sommes pas</strong>.
        </p>
      </div>

      {/* Ce que nous sommes / Ce que nous ne sommes pas */}
      <section className="grid md:grid-cols-2 gap-5" data-testid="charte-identite">
        <div className="rounded-2xl border-2 border-[#16a34a]/25 bg-[#16a34a]/[0.04] p-6">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#16a34a]/15 text-[#16a34a] flex items-center justify-center">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-black">Ce que nous sommes</h2>
          </div>
          <ul className="space-y-2.5">
            {CE_QUE_NOUS_SOMMES.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
                <span className="text-[#16a34a] font-black mt-0.5 flex-shrink-0">✓</span>
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl border-2 border-[#cf2031]/25 bg-[#cf2031]/[0.04] p-6">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#cf2031]/15 text-[#cf2031] flex items-center justify-center">
              <AlertTriangle className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-black">Ce que nous ne sommes pas</h2>
          </div>
          <ul className="space-y-2.5">
            {CE_QUE_NOUS_NE_SOMMES_PAS.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm leading-relaxed">
                <span className="text-[#cf2031] font-black mt-0.5 flex-shrink-0">✗</span>
                <span>{item.text}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Nos 6 engagements */}
      <section data-testid="charte-engagements">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-2">
            <Heart className="w-3.5 h-3.5" />
            Nos 6 engagements
          </div>
          <h2
            className="text-2xl sm:text-3xl font-black tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Ce à quoi nous nous tenons, à chaque publication
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {ENGAGEMENTS.map((e, i) => {
            const Icon = e.icon;
            return (
              <div
                key={i}
                className="rounded-2xl border border-border bg-card p-5"
                data-testid={`charte-engagement-${i}`}
              >
                <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-base font-black mb-1.5 leading-tight">{e.title}</h3>
                <p className="text-sm text-foreground/70 leading-relaxed">{e.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Sources officielles */}
      <section data-testid="charte-sources">
        <div className="mb-6">
          <div className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-2">
            <Landmark className="w-3.5 h-3.5" />
            Nos sources officielles
          </div>
          <h2
            className="text-2xl sm:text-3xl font-black tracking-tight mb-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            D'où viennent nos données ?
          </h2>
          <p className="text-sm text-foreground/70 max-w-2xl leading-relaxed">
            Chaque chiffre, chaque règle, chaque référentiel cité sur CODE 2 ROUES provient
            <strong> exclusivement de sources publiques officielles</strong>. Nous ne créons pas
            de « faits » : nous les traduisons en langage pédagogique.
          </p>
        </div>
        <ul className="space-y-3">
          {SOURCES_OFFICIELLES.map((s, i) => {
            const Icon = s.icon;
            return (
              <li key={i} className="rounded-xl border border-border bg-card p-4 flex items-start gap-3" data-testid={`charte-source-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base font-black leading-snug mb-1">{s.label}</h3>
                  <p className="text-xs sm:text-sm text-foreground/70 leading-relaxed mb-1.5">{s.role}</p>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs font-bold text-[hsl(var(--primary))] hover:underline"
                    data-testid={`charte-source-link-${i}`}
                  >
                    Consulter la source
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {/* Respect des marques tierces */}
      <section className="rounded-2xl border-2 border-amber-400/40 bg-amber-50/40 dark:bg-amber-950/15 p-6" data-testid="charte-marques">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
            <Scale className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-black mb-2">Respect des marques et éditeurs tiers</h2>
            <p className="text-sm text-foreground/80 leading-relaxed mb-2">
              CODE 2 ROUES <strong>reconnaît et respecte</strong> les marques déposées des éditeurs
              spécialisés dans la préparation à l'examen ETM :
              <strong> Codes Rousseau®, ENPC®, Ediser®, Prevent®</strong>, ainsi que les opérateurs
              agréés d'État (La Poste, SGS, Dekra, Bureau Veritas, Pearson VUE, Code'nGo!).
            </p>
            <p className="text-sm text-foreground/80 leading-relaxed">
              Nous <strong>n'entretenons aucun lien commercial</strong> avec ces acteurs et nous ne
              cherchons pas à les concurrencer. Notre positionnement est distinct : nous sommes un
              <em> média éditorial pédagogique</em>, eux sont des <em>préparateurs d'examen</em>.
              Les deux métiers sont complémentaires — mais différents.
            </p>
          </div>
        </div>
      </section>

      {/* Contact / droit de réponse */}
      <section className="rounded-2xl border-2 border-border bg-card p-6 text-center" data-testid="charte-contact">
        <div className="w-12 h-12 rounded-2xl bg-[hsl(var(--primary))]/12 text-[hsl(var(--primary))] flex items-center justify-center mx-auto mb-3">
          <Lock className="w-6 h-6" />
        </div>
        <h2 className="text-xl sm:text-2xl font-black tracking-tight mb-2">
          Une remarque ? Un droit de réponse ?
        </h2>
        <p className="text-sm text-foreground/70 max-w-xl mx-auto leading-relaxed mb-4">
          Si vous identifiez une erreur factuelle, une donnée obsolète, ou une confusion possible
          avec une marque tierce, écrivez-nous. Nous nous engageons à corriger sous 48 h ouvrées.
        </p>
        <div className="flex flex-wrap gap-2 justify-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-bold hover:bg-[hsl(var(--primary))]/90 transition-colors"
            data-testid="charte-contact-cta"
          >
            Nous contacter
          </Link>
          <Link
            to="/mentions-legales"
            className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full border-2 border-border text-sm font-bold hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))] transition-colors"
            data-testid="charte-mentions-cta"
          >
            Mentions légales complètes
          </Link>
        </div>
      </section>

      {/* Auteur / crédit */}
      <footer className="border-t border-border pt-6 text-xs text-muted-foreground text-center">
        <p>
          Charte éditoriale rédigée et signée par <strong>Bruno LLORENS</strong>, ECSR diplômé d'État,
          fondateur et rédacteur en chef de CODE 2 ROUES. Bourgoin-Jallieu, Nord Isère (38). Dernière
          révision : août 2026.
        </p>
      </footer>
    </main>
  );
}
